import testReducer from "../reducers/test";
import { TEST } from "../actions/types";
import { flattenTasks } from "../utills/logicUtils";
import { TASK_TYPES, YESNO, DEVICE_TYPE } from "../actions/constants";
import { isDefaultOrPrototypeTask, isHtmlPrototypeTask, getNewActiveTaskIds } from "../utills/task_functions";

export const usePreviousTaskUrlMiddleware = (store) => (next) => (action) => {
  if (action.type === TEST.UPDATE) {
    const clonedState = { ...store.getState().test };
    const updatedState = testReducer(clonedState, action);

    const { test, tasks = [] } = updatedState?.data || {};
    const isRecordingOff = Number(test?.is_recording_off) === YESNO.YES;
    const isDesktopTest = Number(test?.testing_device) === DEVICE_TYPE.COMPUTER || Number(test?.testing_device) === DEVICE_TYPE.ANY;

    // Fetch Invalid Task Types
    const invalidTaskTypes = getBlockingTaskTypes(isRecordingOff, isDesktopTest);

    // Fetch invalid task id for use previous task url
    const obj = getInvalidContinuation(tasks, invalidTaskTypes);

    if (!obj?.inValidId) return next(action);
    else {
      store.dispatch({
        type: TEST.SHOW_UPDATE_CONFIRMATION_MODAL,
        payload: {
          show: true,
          errorMessage: "",
          confirmationFor: TEST.CONFIRMATION_FOR.TASK_USEPRETASKURL,
          onProceed: () => {
            // Get invalid task id for use previous task url
            const obj = getInvalidContinuation(tasks, invalidTaskTypes);

            const getTask = (task) => {
              const usePrevEnabled = Number(task?.use_prevtask_url) === YESNO.YES;
              return {
                ...task,
                // An HTML prototype task keeps its own prototype link when its continuation is turned off.
                url: usePrevEnabled && !isHtmlPrototypeTask(task) ? "" : task?.url,
                use_prevtask_url: task?.id === obj?.inValidId ? false : task?.use_prevtask_url,
              };
            };

            const updatedTasks = tasks.map((task) => ({ ...getTask(task), subTasks: task?.subTasks?.map(getTask) || [] }));

            store.dispatch({ type: TEST.UPDATE, payload: { data: { ...updatedState?.data, tasks: updatedTasks } } });
          },
          onCancel: () => {
            const filterFun = (t) => t?.type !== TASK_TYPES.EMPTY;
            const filterdTasks = clonedState?.data?.tasks
              .filter(filterFun)
              ?.map((t) => ({ ...t, subTasks: (t?.subTasks || [])?.filter(filterFun) }));

            store.dispatch({
              type: TEST.UPDATE,
              payload: {
                ...clonedState,
                data: { ...clonedState?.data, tasks: filterdTasks },
                activeTaskIds: getNewActiveTaskIds(filterdTasks, clonedState?.activeTaskIds),
              },
            });
          },
        },
      });
    }
  }

  return next(action);
};

// The first task that continues from a previous one across a task type that can't sit in between:
// website and Figma tasks, then HTML prototype tasks (which continue only from each other).
// Figma and HTML prototype tasks can't sit between each other's continued tasks.
function getInvalidContinuation(tasks = [], invalidTaskTypes = []) {
  const forWebsiteAndFigma = getInvalidIdObjForUsePreTask(
    tasks,
    [...invalidTaskTypes, TASK_TYPES.HTML_PROTOTYPE],
    isDefaultOrPrototypeTask
  );
  if (forWebsiteAndFigma?.inValidId) return forWebsiteAndFigma;
  return getInvalidIdObjForUsePreTask(tasks, [...invalidTaskTypes, TASK_TYPES.EXTERNAL_PROTOTYPE], isHtmlPrototypeTask);
}

// Get invalid task id for use previous task url
function getInvalidIdObjForUsePreTask(tasks = [], invalidTaskTypes = [], isChainTask = isDefaultOrPrototypeTask) {
  return flattenTasks(tasks).reduce(
    (obj, task) => {
      if (obj?.inValidId) return obj;

      const isDefaultOrProtoTask = isChainTask(task);
      const usePrevtaskUrl = Number(task?.use_prevtask_url) === YESNO.YES;

      // Initiate check
      if (!obj?.initCheck && isDefaultOrProtoTask) return { ...obj, initCheck: true };

      // Remove hasInvalid flag if it has valid task
      if (obj?.initCheck && isDefaultOrProtoTask && !usePrevtaskUrl) return { ...obj, hasInvalid: false };

      // Add invalid task id if it has invalid task
      if (obj?.initCheck && isDefaultOrProtoTask && usePrevtaskUrl) {
        return { ...obj, inValidId: obj?.hasInvalid ? task?.id : obj?.inValidId };
      }

      // Add hasInvalid flag if it has invalid task
      if (obj?.initCheck && invalidTaskTypes?.includes(Number(task?.type))) {
        return { ...obj, hasInvalid: true };
      }

      return obj;
    },
    { inValidId: null, initCheck: false, hasInvalid: false }
  );
}

// Get Blocking Task Types
export const getBlockingTaskTypes = (isRecordingOff = false, isDesktopTest = false) => {
  // Recording off
  if (isRecordingOff) return [TASK_TYPES.FIVESEC, TASK_TYPES.FIRSTCLICK, TASK_TYPES.TREE, TASK_TYPES.CARDSORT];

  // Mobile/Tablet tests
  if (!isDesktopTest) return [TASK_TYPES.TREE, TASK_TYPES.CARDSORT];

  // TBT + Desktop
  if (isDesktopTest) {
    return [TASK_TYPES.IMAGE, TASK_TYPES.BLANK, TASK_TYPES.FIVESEC, TASK_TYPES.FIRSTCLICK, TASK_TYPES.TREE, TASK_TYPES.CARDSORT];
  }

  return [];
};
